import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const API_BASE_URL = '/api';
const ASSETS_URL = '';

// Abkhaz alphabet letters for navigation
const ALPHABET_LETTERS = [
  'А', 'Б', 'В', 'Г', 'Гь', 'Гә', 'Ӷ', 'Ӷь', 'Ӷә', 
  'Д', 'Дә', 'Е', 'Ж', 'Жь', 'Жә', 'З', 'Ӡ', 'Ӡә', 
  'И', 'К', 'Кь', 'Кә', 'Қ', 'Қь', 'Қә', 'Ҟ', 'Ҟь', 'Ҟә',
  'Л', 'М', 'Н', 'О', 'П', 'Ԥ', 'Р', 'С', 'Т', 'Тә',
  'Ҭ', 'Ҭә', 'У', 'Ф', 'Х', 'Хь', 'Хә', 'Ҳ', 'Ҳә',
  'Ц', 'Цә', 'Ҵ', 'Ҵә', 'Ч', 'Ҷ', 'Ҽ', 'Ҿ',
  'Ш', 'Шь', 'Шә', 'Ы', 'Ҩ', 'Џ', 'Џь'
];

const Dictionary = ({ isOpen, onClose }) => {
  const [dictionaryData, setDictionaryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLetter, setActiveLetter] = useState(null);
  const contentRef = useRef(null);
  const letterRefs = useRef({});

  useEffect(() => {
    if (isOpen) {
      fetchDictionary();
    }
  }, [isOpen]);

  const fetchDictionary = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/dictionary`);
      if (response.ok) {
        const data = await response.json();
        setDictionaryData(data);
      }
    } catch (error) {
      console.error('Failed to fetch dictionary:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToLetter = (letter) => {
    setActiveLetter(letter);
    const element = letterRefs.current[letter];
    if (element && contentRef.current) {
      const containerTop = contentRef.current.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;
      const offset = elementTop - containerTop;
      contentRef.current.scrollTop += offset - 10;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <img 
              src={`${ASSETS_URL}/assets/images/dic_icon.png`}
              alt="ლექსიკონი"
              className="w-8 h-8"
            />
            <h2 className="text-xl font-bold text-gray-900 font-['bpg-caps']">
              ლექსიკონი
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close dictionary"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Alphabet Navigation */}
        <div className="p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-wrap gap-1 justify-center">
            {ALPHABET_LETTERS.map((letter) => (
              <button
                key={letter}
                onClick={() => scrollToLetter(letter)}
                className={`px-1.5 py-0.5 text-xs font-medium rounded transition-colors min-w-[24px]
                  ${activeLetter === letter 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 hover:bg-blue-100 hover:text-blue-600 border border-gray-200'
                  }
                  ${!dictionaryData.some(s => s.letter === letter) ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                disabled={!dictionaryData.some(s => s.letter === letter)}
              >
                {letter}
              </button>
            ))}
          </div>
        </div>

        {/* Dictionary Content */}
        <div 
          ref={contentRef}
          className="flex-1 overflow-y-auto p-4"
        >
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 font-['bpg-caps']">იტვირთება...</div>
            </div>
          ) : dictionaryData.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500 font-['bpg-caps']">ლექსიკონი ცარიელია</div>
            </div>
          ) : (
            <div className="space-y-4">
              {dictionaryData.map((section) => (
                <div 
                  key={section.letter}
                  ref={(el) => letterRefs.current[section.letter] = el}
                >
                  {/* Letter Header */}
                  <div className="sticky top-0 bg-blue-600 text-white px-3 py-1 rounded-lg mb-2 shadow-md">
                    <h3 className="text-sm font-bold">{section.letter}</h3>
                  </div>
                  
                  {/* Entries */}
                  <div className="space-y-1 pl-2">
                    {section.entries.map((entry, index) => (
                      <div 
                        key={index}
                        className="px-3 py-1.5 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors flex flex-wrap items-baseline gap-x-2"
                      >
                        <span className="font-semibold text-gray-900 shrink-0">
                          {entry.word}
                        </span>
                        {entry.grammar && (
                          <span className="text-sm text-blue-600 italic shrink-0">
                            ({entry.grammar})
                          </span>
                        )}
                        <span className="text-gray-700">
                          {entry.translation}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Dictionary Button Component to be used in headers
export const DictionaryButton = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
      aria-label="Open dictionary"
      title="ლექსიკონი"
    >
      <img 
        src={`${ASSETS_URL}/assets/images/dic_icon.png`}
        alt="ლექსიკონი"
        className="w-6 h-6"
      />
    </button>
  );
};

export default Dictionary;
